import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { ContractPartner, ContractPartnerFormData } from '@/types';
import { Edit, Mail, Phone, Plus, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import ContractPartnerDialog from './ContractPartnerDialog';

interface ContractPartnerManagerProps {
  contractPartners: ContractPartner[];
  onAdd: (data: ContractPartnerFormData) => void;
  onEdit: (id: string, data: ContractPartnerFormData) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

/**
 * Contract Partner Manager Component
 * Handles listing, adding, editing, and deleting contract partners
 * @param contractPartners - Array of contract partners to display
 * @param onAdd - Callback when adding a new contract partner
 * @param onEdit - Callback when editing an existing contract partner
 * @param onDelete - Callback when deleting a contract partner
 * @param isLoading - Loading state for operations
 */
export default function ContractPartnerManager({
  contractPartners,
  onAdd,
  onEdit,
  onDelete,
  isLoading = false,
}: ContractPartnerManagerProps) {
  const { t: tForms } = useFormsTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<
    ContractPartner | undefined
  >();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<
    ContractPartner | undefined
  >();

  const handleAddClick = () => {
    setEditingPartner(undefined);
    setDialogOpen(true);
  };

  const handleEditClick = (partner: ContractPartner) => {
    setEditingPartner(partner);
    setDialogOpen(true);
  };

  const handleDeleteClick = (partner: ContractPartner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleSave = (data: ContractPartnerFormData) => {
    if (editingPartner) {
      onEdit(editingPartner.id, data);
    } else {
      onAdd(data);
    }
    setDialogOpen(false);
    setEditingPartner(undefined);
  };

  const confirmDelete = () => {
    if (partnerToDelete) {
      onDelete(partnerToDelete.id);
      setDeleteDialogOpen(false);
      setPartnerToDelete(undefined);
    }
  };

  const formatRole = (role: string) => {
    const roleKey = `contractPartner.roles.${role}`;
    return tForms(roleKey);
  };

  const formatLanguage = (language: string) => {
    const langKey = `contractPartner.languages.${language}`;
    return tForms(langKey);
  };

  return (
    <div className='space-y-6'>
      {/* Header with Add Button */}
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-semibold'>
            {tForms('contractPartner.title')}
          </h3>
          <p className='text-sm text-muted-foreground'>
            {tForms('contractPartner.description')}
          </p>
        </div>
        <Button onClick={handleAddClick} disabled={isLoading}>
          <Plus className='h-4 w-4 mr-2' />
          {tForms('contractPartner.addButton')}
        </Button>
      </div>

      {/* Contract Partners Table */}
      {contractPartners.length === 0 ? (
        <div className='border rounded-lg p-8 text-center'>
          <User className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
          <h3 className='text-lg font-medium mb-2'>
            {tForms('contractPartner.noPartnersTitle')}
          </h3>
          <p className='text-muted-foreground mb-4'>
            {tForms('contractPartner.noPartnersDescription')}
          </p>
          <Button onClick={handleAddClick} disabled={isLoading}>
            <Plus className='h-4 w-4 mr-2' />
            {tForms('contractPartner.addButton')}
          </Button>
        </div>
      ) : (
        <div className='border rounded-lg'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {tForms('contractPartner.tableHeaders.name')}
                </TableHead>
                <TableHead>
                  {tForms('contractPartner.tableHeaders.role')}
                </TableHead>
                <TableHead>
                  {tForms('contractPartner.tableHeaders.language')}
                </TableHead>
                <TableHead>
                  {tForms('contractPartner.tableHeaders.contact')}
                </TableHead>
                <TableHead>
                  {tForms('contractPartner.tableHeaders.status')}
                </TableHead>
                <TableHead className='text-right'>
                  {tForms('contractPartner.tableHeaders.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractPartners.map(partner => (
                <TableRow key={partner.id}>
                  <TableCell>
                    <div className='flex flex-col'>
                      <span className='font-medium'>
                        {partner.title ? `${partner.title} ` : ''}
                        {partner.firstName} {partner.lastName}
                      </span>
                      {partner.isRepresentative && (
                        <Badge variant='secondary' className='w-fit mt-1'>
                          {tForms('contractPartner.badges.representative')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className='capitalize'>
                      {formatRole(partner.role)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span>{formatLanguage(partner.language)}</span>
                  </TableCell>
                  <TableCell>
                    <div className='flex flex-col space-y-1'>
                      <div className='flex items-center text-sm'>
                        <Mail className='h-3 w-3 mr-1 text-muted-foreground' />
                        <span className='text-sm'>{partner.email}</span>
                      </div>
                      {partner.phone && (
                        <div className='flex items-center text-sm'>
                          <Phone className='h-3 w-3 mr-1 text-muted-foreground' />
                          <span className='text-sm'>{partner.phone}</span>
                        </div>
                      )}
                      {partner.mobile && !partner.phone && (
                        <div className='flex items-center text-sm'>
                          <Phone className='h-3 w-3 mr-1 text-muted-foreground' />
                          <span className='text-sm'>{partner.mobile}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className='flex flex-col space-y-1'>
                      {partner.hasDifferentAddress && (
                        <Badge variant='outline' className='w-fit'>
                          {tForms('contractPartner.badges.differentAddress')}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className='text-right'>
                    <div className='flex items-center justify-end space-x-2'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleEditClick(partner)}
                        disabled={isLoading}
                      >
                        <Edit className='h-4 w-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleDeleteClick(partner)}
                        disabled={isLoading}
                        className='text-destructive hover:text-destructive'
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <ContractPartnerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contractPartner={editingPartner}
        onSave={handleSave}
        isLoading={isLoading}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tForms('contractPartner.deleteDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tForms('contractPartner.deleteDialogDescription', {
                firstName: partnerToDelete?.firstName,
                lastName: partnerToDelete?.lastName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>
              {tForms('contractPartner.buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isLoading}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {isLoading ? (
                <div className='flex items-center space-x-2'>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                  <span>{tForms('contractPartner.buttons.deleting')}</span>
                </div>
              ) : (
                tForms('contractPartner.buttons.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
